import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import createCanonicalUrl from "../../utils/createCanonicalUrl";

const siteLanguage = "ar";

function TitleAndMeta({
  title,
  slug,
  tags,
  categories,
  type = "website",
  description,
  ogImage,
  ogImageAlt,
  createdAt,
  updatedAt: dateModified,
  publish_at: datePublished = createdAt,
  articleAuthor,
}) {
  const {
    strapiSiteMeta: {
      siteUrl,
      title: siteName,
      description: defaultDescription,
    },
    strapiAuthor: {
      name: defaultAuthorName,
      avatar: { url: defaultImgSlug },
    },
  } = useStaticQuery(graphql`
    query {
      strapiSiteMeta {
        site_url
        title
        description
      }
      strapiAuthor {
        name
        avatar {
          url
        }
      }
    }
  `);

  const canonicalUrl = slug ? createCanonicalUrl(slug) : siteUrl;
  const renderDesc = description || defaultDescription;
  const renderTitle = title
    ? `${title} | ${siteName}`
    : `${siteName} - ${defaultDescription}`;

  const renderAuthor = articleAuthor.author || defaultAuthorName;
  const renderOgImage = ogImage || defaultImgSlug;

  // schema.org in JSONLD format
  // https://developers.google.com/search/docs/guides/intro-structured-data
  // You can fill out the 'author', 'creator' with more data or another type (e.g. 'Organization')
  const schemaOrgWebPage = {
    "@context": "http://schema.org",
    "@type": "WebPage",
    url: canonicalUrl,
    headline: renderTitle,
    inLanguage: siteLanguage,
    mainEntityOfPage: siteUrl,
    description: renderDesc,
    name: renderTitle,
    author: {
      "@type": "Person",
      name: renderAuthor,
    },
    copyrightHolder: {
      "@type": "Person",
      name: renderAuthor,
    },
    copyrightYear: new Date().getFullYear(),
    creator: {
      "@type": "Person",
      name: renderAuthor,
    },
    publisher: {
      "@type": "Person",
      name: renderAuthor,
    },
    datePublished,
    dateModified,
    image: {
      "@type": "ImageObject",
      url: renderOgImage,
    },
  };

  // Initial breadcrumb list
  const itemListElement = [
    {
      "@type": "ListItem",
      item: {
        "@id": siteUrl,
        name: "Homepage",
      },
      position: 1,
    },
  ];

  let schemaArticle = null;

  if (type === "article") {
    schemaArticle = {
      "@context": "http://schema.org",
      "@type": "Article",
      author: {
        "@type": "Person",
        name: renderAuthor,
      },
      copyrightHolder: {
        "@type": "Person",
        name: renderAuthor,
      },
      copyrightYear: "2019",
      creator: {
        "@type": "Person",
        name: renderAuthor,
      },
      publisher: {
        "@type": "Organization",
        name: renderAuthor,
      },
      datePublished,
      dateModified,
      description: renderDesc,
      headline: renderTitle,
      inLanguage: siteLanguage,
      url: canonicalUrl,
      name: renderTitle,
      image: {
        "@type": "ImageObject",
        representativeOfPage: true,
        url: renderOgImage,
      },
      mainEntityOfPage: canonicalUrl,
      isAccessibleForFree: true,
    };
    // Push current blogpost into breadcrumb list
    itemListElement.push({
      "@type": "ListItem",
      item: {
        "@id": canonicalUrl,
        name: renderTitle,
      },
      position: 2,
    });
  }

  const breadcrumb = {
    "@context": "http://schema.org",
    "@type": "BreadcrumbList",
    description: "Breadcrumbs list",
    name: "Breadcrumbs",
    itemListElement,
  };

  return (
    <>
      {/* <meta
        name="google-site-verification"
        content="6bY_1XXHjvkWX_G3O1CvFGA-L5RqpOvE2-_PdZ23xmM"
      /> */}
      <meta
        name="google-site-verification"
        content="pzOJ-dCN7et3_25Uhbo0RtKEdLDoUq7EuomrMO3OPtA"
      />
      <title>{renderTitle}</title>
      <meta name="robots" content="all" />
      <meta name="description" content={renderDesc} />
      <meta name="author" content={renderAuthor} />
      <meta name="keywords" content={tags || renderAuthor} />
      <meta property="og:title" content={renderTitle} />
      <meta property="og:description" content={renderDesc} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="ar" />
      <meta property="og:url" content={canonicalUrl} />
      {renderOgImage && <meta property="og:image" content={renderOgImage} />}
      {renderOgImage && ogImageAlt && (
        <meta property="og:image:alt" content={ogImageAlt} />
      )}
      {type === "article" && articleAuthor.fb && (
        <meta
          property="article:author"
          content={`https://www.facebook.com/${articleAuthor.fb}`}
        />
      )}
      {type === "article" && articleAuthor.fb && (
        <meta
          property="article:publisher"
          content={`https://www.facebook.com/${articleAuthor.fb}`}
        />
      )}
      {type === "article" && (
        <meta property="article:section" content={categories || "Literature"} />
      )}
      {slug && <link rel="canonical" href={canonicalUrl} />}
      <link rel="alternate" href={siteUrl} hrefLang="ar" />
      {renderOgImage && (
        <meta property="twitter:image" content={renderOgImage} />
      )}
      {renderOgImage && ogImageAlt && (
        <meta property="twitter:image:alt" content={ogImageAlt} />
      )}
      <meta property="twitter:card" content="summary" />
      {type === "article" && articleAuthor.twitter && (
        <meta property="twitter:creator" content={articleAuthor.twitter} />
      )}
      {type === "article" && articleAuthor.twitter && (
        <meta property="twitter:site" content={articleAuthor.twitter} />
      )}

      <meta property="twitter:title" content={renderTitle} />
      <meta property="twitter:description" content={renderDesc} />
      <meta name="twitter:domain" content={siteUrl} />

      {!type !== "article" && (
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgWebPage)}
        </script>
      )}
      {type === "article" && (
        <script type="application/ld+json">
          {JSON.stringify(schemaArticle)}
        </script>
      )}
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
    </>
  );
}

export default TitleAndMeta;
